import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, X } from 'lucide-react';

interface Props {
  title: string;
  children: ReactNode;
  onClose: () => void;
  onBack?: () => void;
}

export default function HomeSheet({ title, children, onClose, onBack }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [closing, setClosing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startY = useRef<number | null>(null);
  const historyOwned = useRef(false);
  const dismissRef = useRef<() => void>(() => {});

  const dismiss = () => {
    if (closing) return;
    setClosing(true);
    (document.activeElement as HTMLElement | null)?.blur();
    timer.current = setTimeout(() => {
      if (historyOwned.current) { historyOwned.current = false; history.back(); }
      closeRef.current();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
  };
  dismissRef.current = dismiss;

  useEffect(() => {
    const dialog = dialogRef.current;
    const previous = document.activeElement as HTMLElement | null;
    dialog?.showModal();
    dialog?.querySelector<HTMLButtonElement>('[data-sheet-close]')?.focus({ preventScroll: true });
    // Defer ownership one frame so StrictMode's setup/cleanup rehearsal cannot
    // create a phantom Back event that immediately dismisses a newly opened sheet.
    const historyFrame = requestAnimationFrame(() => {
      history.pushState({ ...history.state, lavynHomeSheet: true }, '', window.location.href);
      historyOwned.current = true;
    });
    const pop = () => { historyOwned.current = false; dismissRef.current(); };
    window.addEventListener('popstate', pop);
    return () => {
      cancelAnimationFrame(historyFrame);
      if (timer.current) clearTimeout(timer.current);
      window.removeEventListener('popstate', pop);
      dialog?.close();
      if (historyOwned.current) { historyOwned.current = false; history.back(); }
      if (previous?.isConnected && !(previous instanceof HTMLInputElement) && !(previous instanceof HTMLTextAreaElement)) previous.focus({ preventScroll: true });
    };
  }, []);

  return <dialog ref={dialogRef} className={`home-sheet-dialog ${closing ? 'is-closing' : ''}`} aria-labelledby="home-sheet-title" onCancel={event => { event.preventDefault(); dismiss(); }} onPointerDown={event => { if (event.target === event.currentTarget) dismiss(); }}>
    <div ref={panelRef} className="home-sheet-panel">
      <div className="home-sheet-handle" aria-label="Drag down to dismiss" role="button" tabIndex={0}
        onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') dismiss(); }}
        onPointerDown={event => { startY.current = event.clientY; event.currentTarget.setPointerCapture(event.pointerId); }}
        onPointerMove={event => { if (startY.current !== null && panelRef.current) panelRef.current.style.transform = `translateY(${Math.max(0, event.clientY - startY.current)}px)`; }}
        onPointerUp={event => { const distance = event.clientY - (startY.current ?? event.clientY); startY.current = null; if (panelRef.current) panelRef.current.style.transform = ''; if (distance > 65) dismiss(); }}
        onPointerCancel={() => { startY.current = null; if (panelRef.current) panelRef.current.style.transform = ''; }}><span /></div>
      <header className="home-sheet-header">{onBack && <button type="button" onClick={onBack} aria-label="Back"><ArrowLeft size={19} /></button>}<h2 id="home-sheet-title">{title}</h2><button data-sheet-close type="button" onClick={dismiss} aria-label="Close sheet"><X size={19} /></button></header>
      <div className="home-sheet-content">{children}</div>
    </div>
  </dialog>;
}
